/**
 * Odometer roll for the stats section (partials/section-stats.php) and any
 * other number marked with data-pcm-odometer (the About Joy stats).
 *
 * The page prints the final value as plain text, so crawlers, no-JS
 * visitors and screen readers always get the real number. As a value comes
 * into view, each digit is swapped for a reel that spins up from
 * 0 and clicks into place with a small spring overshoot; the right-most digit
 * of each number spins an extra turn. Anything that isn't a digit ("%", "$",
 * "-", ".", ",", "Days", "Over") stays as typed, and a value with no digits at
 * all ("QR", "High ROI") rolls up into place as a word instead.
 *
 * Cost: no per-frame script. The reels are moved by CSS transform transitions
 * (compositor-only), and the value's own text stays in flow, hidden, so
 * nothing around it shifts; each reel is pinned to its digit's measured spot.
 * Styles: sass/partials/_stat-odometer.scss. Reels are parked at 0 while the
 * value is still just below the fold and released as it comes into view, and the
 * observers let go of each value after it plays. Values already on screen
 * when the page loads, and visitors who prefer reduced motion, are left alone.
 */
(function () {
    'use strict';

    var VALUE = '.pcm-section-stats__value, [data-pcm-odometer]';
    // A value's number and unit may be separate spans (the stats section, or
    // parts marked data-pcm-odometer-part); otherwise the value is one
    // plain-text element.
    var PARTS = '.pcm-section-stats__stat, .pcm-section-stats__unit, [data-pcm-odometer-part]';
    var ITEM_STAGGER = 120;
    var DIGIT_STAGGER = 40;
    var easeCache = {};

    var supportsLinear = window.CSS && CSS.supports && CSS.supports('transition-timing-function', 'linear(0, 1)');

    // A damped spring sampled into a CSS linear() easing. `overshoot` is the
    // fraction of the travel distance it swings past the end, so a reel that
    // travels 19 slots gets a smaller fraction than a word that travels one,
    // and every reel overshoots by roughly the same ~0.4 of a digit.
    function springEasing(overshoot) {
        var key = overshoot.toFixed(3);

        if (easeCache[key]) {
            return easeCache[key];
        }

        var log = Math.log(overshoot);
        var zeta = -log / Math.sqrt(Math.PI * Math.PI + log * log);
        var omega = 6.9 / zeta; // envelope reaches 0.1% at t = 1
        var omegaD = omega * Math.sqrt(1 - zeta * zeta);
        var points = [];

        for (var i = 0; i < 48; i++) {
            var t = i / 48;
            var x = 1 - Math.exp(-zeta * omega * t) * (Math.cos(omegaD * t) + (zeta * omega / omegaD) * Math.sin(omegaD * t));
            points.push(+x.toFixed(4));
        }

        points.push(1);
        easeCache[key] = 'linear(' + points.join(',') + ')';

        return easeCache[key];
    }

    function el(tag, className, text) {
        var node = document.createElement(tag);
        node.className = className;

        if (text !== undefined) {
            node.textContent = text;
        }

        return node;
    }

    function place(piece, x, width) {
        piece.style.left = x.toFixed(4) + 'em';

        if (width !== undefined) {
            piece.style.width = width.toFixed(4) + 'em';
        }

        return piece;
    }

    function reel(digit, turns, itemIndex, digitIndex) {
        var to = digit + 10 * turns;
        var wrap = el('span', 'pcm-odo');
        var strip = el('span', 'pcm-odo__reel');

        for (var k = 0; k <= to; k++) {
            strip.appendChild(el('span', '', String(k % 10)));
        }

        strip.style.setProperty('--pcm-odo-to', to);
        strip.style.transitionDelay = (itemIndex * ITEM_STAGGER + digitIndex * DIGIT_STAGGER) + 'ms';

        if (supportsLinear) {
            strip.style.transitionTimingFunction = springEasing(Math.min(0.45, 0.38 / to + 0.004));
        }

        wrap.appendChild(strip);

        return wrap;
    }

    function wordSlot(text, itemIndex) {
        var wrap = el('span', 'pcm-odo pcm-odo--word');
        var mover = el('span', 'pcm-odo__word', text);

        mover.style.transitionDelay = (itemIndex * ITEM_STAGGER) + 'ms';

        if (supportsLinear) {
            mover.style.transitionTimingFunction = springEasing(0.22);
        }

        wrap.appendChild(mover);

        return wrap;
    }

    // Reads where every character of a plain-text element sits, in em from
    // the start of its text, so the pieces laid over it land exactly on the
    // kerned glyphs ("7." is set much tighter than two separate boxes).
    // Read-only: called for the whole batch before anything is written.
    function measure(host) {
        var node = host.firstChild;

        if (host.childNodes.length !== 1 || !node || node.nodeType !== 3 || !node.data.trim()) {
            return null;
        }

        var size = parseFloat(window.getComputedStyle(host).fontSize) || 1;
        var range = document.createRange();
        var chars = [];

        range.selectNodeContents(host);

        var origin = range.getBoundingClientRect().left;

        for (var i = 0; i < node.data.length; i++) {
            range.setStart(node, i);
            range.setEnd(node, i + 1);

            var box = range.getBoundingClientRect();

            chars.push({ c: node.data.charAt(i), x: (box.left - origin) / size, w: box.width / size });
        }

        return { host: host, node: node, chars: chars, size: size };
    }

    // Wraps the text in a box: the text stays in flow but hidden, so
    // wrapping, kerning and the baseline are exactly what the page had, and
    // the visible pieces sit on a layer over it. Returns the box and how many
    // moving parts it made.
    function build(measured, itemIndex, counter, wordsOnly) {
        var box = el('span', 'pcm-odo-box');
        var text = el('span', 'pcm-odo__text');
        var layer = el('span', 'pcm-odo__layer');
        var moving = 0;

        box.setAttribute('aria-hidden', 'true');
        measured.host.insertBefore(box, measured.node);
        text.appendChild(measured.node);
        box.appendChild(text);

        if (wordsOnly) {
            layer.appendChild(wordSlot(measured.node.data, itemIndex));
            moving = 1;
        } else {
            // Digits counted from the right within each number, so the last
            // digit of every number gets the extra turn.
            var chars = measured.chars;
            var digitsLeft = 0;

            for (var i = chars.length - 1; i >= 0; i--) {
                if (/\d/.test(chars[i].c)) {
                    chars[i].fromRight = digitsLeft++;
                } else if (chars[i].c !== ',' && chars[i].c !== '.') {
                    digitsLeft = 0;
                }
            }

            chars.forEach(function (ch) {
                if (/\d/.test(ch.c)) {
                    layer.appendChild(place(reel(+ch.c, ch.fromRight === 0 ? 2 : 1, itemIndex, counter.digit++), ch.x, ch.w));
                    moving++;
                } else if (ch.c.trim()) {
                    layer.appendChild(place(el('span', 'pcm-odo__char', ch.c), ch.x));
                }
            });
        }

        box.appendChild(layer);

        return { box: box, size: measured.size, moving: moving };
    }

    // Measures, then swaps, a batch of values for reels parked at 0. Called
    // while the values are still just below the fold, so nobody sees the
    // number reset. Returns the values that have something to roll.
    function arm(batch) {
        var plans = batch.map(function (value) {
            var parts = value.querySelectorAll(PARTS);
            var hosts = parts.length ? Array.prototype.slice.call(parts) : [value];
            var fullText = value.textContent.replace(/\s+/g, ' ').trim();

            return {
                value: value,
                fullText: fullText,
                wordsOnly: !/\d/.test(fullText),
                measured: hosts.map(measure).filter(Boolean)
            };
        });
        var boxes = [];

        var ready = plans.filter(function (plan, order) {
            var counter = { digit: 0 };
            var moving = 0;

            if (!plan.measured.length) {
                return false;
            }

            plan.value.classList.add('pcm-odo-armed', 'pcm-odo-on');

            // Values arriving together (a desktop row) roll left to right;
            // one arriving alone (a phone row) starts at once.
            plan.measured.forEach(function (measured) {
                var built = build(measured, order, counter, plan.wordsOnly);

                boxes.push(built);
                moving += built.moving;
            });

            // Screen readers hear the final value once, not the reels. The
            // reels stay in place after they stop, so nothing is swapped back.
            plan.value.appendChild(el('span', 'visually-hidden', plan.fullText));

            return moving > 0;
        });

        // Each reel slot is one line tall. Read every box's line height in
        // one pass (one layout), then write them, rather than trusting a
        // line-height that may be "normal".
        var heights = boxes.map(function (built) {
            return built.box.getBoundingClientRect().height;
        });

        boxes.forEach(function (built, i) {
            if (heights[i] > 0) {
                built.box.style.setProperty('--pcm-odo-lh', (heights[i] / built.size).toFixed(4));
            }
        });

        return ready.map(function (plan) {
            return plan.value;
        });
    }

    // One forced layout so the parked reels are committed, then every value in
    // the batch is released together.
    function release(values) {
        void document.body.offsetWidth;

        values.forEach(function (value) {
            value.classList.remove('pcm-odo-armed');
        });
    }

    function start() {
        var values = document.querySelectorAll(VALUE);

        if (!values.length || !('IntersectionObserver' in window) || typeof WeakSet !== 'function') {
            return;
        }

        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        var reported = new WeakSet();
        var armed = new WeakSet();
        var released = new WeakSet();
        var player;

        // Arms each value as it comes within a quarter screen of the bottom
        // edge, before it is visible. Values already on screen at load are
        // left alone rather than reset to zero in front of the visitor.
        var armer = new IntersectionObserver(function (entries) {
            var batch = [];
            var visibleNow = [];

            entries.forEach(function (entry) {
                var value = entry.target;
                var firstReport = !reported.has(value);
                var visible = entry.boundingClientRect.top < window.innerHeight && entry.boundingClientRect.bottom > 0;

                reported.add(value);

                if (!entry.isIntersecting) {
                    return;
                }

                armer.unobserve(value);

                if (firstReport && visible) {
                    player.unobserve(value);
                    return;
                }

                batch.push(value);

                // Scrolled in from above, or jumped straight to it.
                if (visible) {
                    visibleNow.push(value);
                }
            });

            if (!batch.length) {
                return;
            }

            var ready = arm(batch);
            var playNow = [];

            batch.forEach(function (value) {
                if (ready.indexOf(value) === -1) {
                    player.unobserve(value);
                    return;
                }

                armed.add(value);

                if (visibleNow.indexOf(value) !== -1) {
                    released.add(value);
                    player.unobserve(value);
                    playNow.push(value);
                }
            });

            if (playNow.length) {
                release(playNow);
            }
        }, { rootMargin: '0px 0px 25% 0px' });

        // Starts the roll as soon as the top of the number clears the bottom
        // 5% of the screen.
        player = new IntersectionObserver(function (entries) {
            var batch = [];

            entries.forEach(function (entry) {
                var value = entry.target;

                if (!entry.isIntersecting || !armed.has(value) || released.has(value)) {
                    return;
                }

                released.add(value);
                player.unobserve(value);
                batch.push(value);
            });

            if (batch.length) {
                release(batch);
            }
        }, { rootMargin: '0px 0px -5% 0px' });

        Array.prototype.forEach.call(values, function (value) {
            armer.observe(value);
            player.observe(value);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
}());
